import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from 'src/api/axiosInstance';
import store from 'src/redux/store';
import {
  setStepData,
  setStepLoading,
  selectStepData,
  selectIsStepLoading,
  selectIsCacheValid,
} from 'src/redux/slices/masterDataSlice';

const FORM_TYPE = 'emp';
const ENDPOINT = '/rest/candidate/fetchEmpMastersByStep';

/**
 * Custom Hook: Fetch and cache employee master data for the active step.
 *
 * Uses store.getState() inside fetchStepData to read isCacheValid at CALL TIME,
 * avoiding stale-closure bugs where an outdated isCacheValid=true silently
 * skips the API call.
 *
 * @param {number} step - Active step index (0–7)
 * @returns {{ data: object|null, isLoading: boolean, refresh: () => void }}
 */
export function useEmpMasterData(step) {
  const dispatch = useDispatch();

  // Read data & loading state from Redux for the UI
  const data = useSelector(selectStepData(FORM_TYPE, step));
  const isLoading = useSelector(selectIsStepLoading(FORM_TYPE, step));

  // All 8 steps (0–7) have step-specific master data
  const stepHasMasterData = step >= 0 && step <= 7;

  /**
   * Fetches step master data from the backend.
   * Reads cache validity from store.getState() at call time (not from a stale closure).
   */
  const fetchStepData = useCallback(
    async (forceRefresh = false) => {
      // 1. Skip if step has no master data defined
      if (!stepHasMasterData) return;

      // 2. Read isCacheValid from Redux state RIGHT NOW (not from stale closure)
      const currentState = store.getState();
      const isCacheValidNow = selectIsCacheValid(FORM_TYPE, step)(currentState);

      // 3. Skip API call if cache is still valid AND this is not a forced refresh
      if (isCacheValidNow && !forceRefresh) return;

      // 4. Read token at call time
      const token = sessionStorage.getItem('token');

      dispatch(setStepLoading({ formType: FORM_TYPE, step, isLoading: true }));

      try {
        const res = await axiosInstance.get(`${ENDPOINT}?step=${step}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Save response into Redux cache
        dispatch(setStepData({ formType: FORM_TYPE, step, data: res.data }));
      } catch (err) {
        console.error(`[useEmpMasterData] Failed to load step ${step} master data:`, err);
        // Store empty object so loading spinner stops; step still renders gracefully
        dispatch(setStepData({ formType: FORM_TYPE, step, data: {} }));
      }
    },
    // fetchStepData only needs to be recreated when step or stepHasMasterData changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, stepHasMasterData]
  );

  // Re-fetch whenever the active step changes (includes initial mount)
  useEffect(() => {
    fetchStepData(false);
  }, [fetchStepData, step]);

  /** Force re-fetch from backend */
  const refresh = useCallback(() => {
    fetchStepData(true);
  }, [fetchStepData]);

  return { data, isLoading, refresh };
}
